import { safeJsonParse } from '../utils/utils';
import { initTranslatedItems } from '../utils/translationProcessor';
import { DOB_ERROR_MSG, DOB_LABELS } from './dateOfBirth';
import { LOOKUP_PAGE_TITLE } from '../constants';
import { ALL_FIELDS_REQUIRED } from './form';

export const PAGE_TITLE = 'Enter their personal info';
export const PAGE_TYPE = 'personal-info';
export const TITLE = 'Who are you \ncaring for?';
export const TITLE_SELF = 'Enter your information';
export const TITLE_PET = 'Enter your pet\'s details';
export const DESCRIPTION = 'We’ll look for their records.';
export const DESCRIPTION_SELF = 'We’ll look for your records.';
export const DESCRIPTION_PET = 'We\'ll look for their records.';

export const SUBMIT_BUTTON_TEXT = 'Search';
export const CANCEL_BUTTON_TEXT = 'Cancel';

export const PERSONAL_INFO_FORM_LABELS = {
  FIRST_NAME: 'First name',
  LAST_NAME: 'Last name',
  DATE_OF_BIRTH: 'Date of birth',
  BIRTH_YEAR: 'Birth year',
  PHONE_NUMBER: 'Phone number'
};

export const PERSONAL_INFO_ERROR_TITLES = {
  DEFAULT: 'We need you to check something'
};

export const PERSONAL_INFO_VALIDATION = {
  FIRST_NAME_REQUIRED: 'Enter a first name',
  FIRST_NAME_INVALID: 'First name cannot exceed 40 characters',
  LAST_NAME_REQUIRED: 'Enter a last name',
  LAST_NAME_INVALID: 'Last name cannot exceed 40 characters',
  DATE_OF_BIRTH_REQUIRED: 'Enter an 8-digit date of birth',
  DATE_OF_BIRTH_INVALID: 'Enter a valid 8-digit date of birth',
  PHONE_NUMBER_REQUIRED: 'Enter a 10-digit phone number',
  PHONE_NUMBER_INVALID: 'Enter a valid 10-digit phone number\n'
};

export const PERSONAL_INFO_FORM_HELP_TEXT = {
  DATE_OF_BIRTH: 'MMDDYYYY',
  DATE_OF_BIRTH_YYYY: 'Enter 4 digits',
  PHONE_NUMBER: 'Enter the number on file with \n the pharmacy.'
};

// Translation configuration for personal info page
export const PERSONAL_INFO_TRANSLATION_CONFIG = [
  { key: 'DESCRIPTION', value: DESCRIPTION, message: DESCRIPTION },
  { key: 'DESCRIPTION_SELF', value: DESCRIPTION_SELF, message: DESCRIPTION_SELF },
  { key: 'DESCRIPTION_PET', value: DESCRIPTION_PET, message: DESCRIPTION_PET },
  {
    key: 'PERSONAL_INFO_ERROR_TITLES',
    value: PERSONAL_INFO_ERROR_TITLES,
    message: JSON.stringify(PERSONAL_INFO_ERROR_TITLES)
  },
  {
    key: 'PERSONAL_INFO_FORM_HELP_TEXT',
    value: PERSONAL_INFO_FORM_HELP_TEXT,
    message: JSON.stringify(PERSONAL_INFO_FORM_HELP_TEXT)
  },
  {
    key: 'PERSONAL_INFO_FORM_LABELS',
    value: PERSONAL_INFO_FORM_LABELS,
    message: JSON.stringify(PERSONAL_INFO_FORM_LABELS)
  },
  {
    key: 'PERSONAL_INFO_VALIDATION',
    value: PERSONAL_INFO_VALIDATION,
    message: JSON.stringify(PERSONAL_INFO_VALIDATION)
  },
  { key: 'SUBMIT_BUTTON_TEXT', value: SUBMIT_BUTTON_TEXT, message: SUBMIT_BUTTON_TEXT },
  { key: 'CANCEL_BUTTON_TEXT', value: CANCEL_BUTTON_TEXT, message: CANCEL_BUTTON_TEXT },
  { key: 'TITLE', value: TITLE, message: TITLE },
  { key: 'TITLE_SELF', value: TITLE_SELF, message: TITLE_SELF },
  { key: 'TITLE_PET', value: TITLE_PET, message: TITLE_PET },
  { key: 'DOB_ERROR_MSG', value: DOB_ERROR_MSG, message: JSON.stringify(DOB_ERROR_MSG) },
  { key: 'DOB_LABELS', value: DOB_LABELS, message: JSON.stringify(DOB_LABELS) },
  { key: 'LOOKUP_PAGE_TITLE', value: LOOKUP_PAGE_TITLE, message: LOOKUP_PAGE_TITLE },
  { key: 'ALL_FIELDS_REQUIRED', value: ALL_FIELDS_REQUIRED, message: ALL_FIELDS_REQUIRED }
];

// Helper function to process translated items
export const processPersonalInfoTranslations = (translatedItems: any) => {
  translatedItems = initTranslatedItems(translatedItems);

  // Destructure simple string values with fallbacks
  const {
    [DESCRIPTION]: DESCRIPTION_TRANSLATED = DESCRIPTION,
    [DESCRIPTION_SELF]: DESCRIPTION_SELF_TRANSLATED = DESCRIPTION_SELF,
    [DESCRIPTION_PET]: DESCRIPTION_PET_TRANSLATED = DESCRIPTION_PET,
    [SUBMIT_BUTTON_TEXT]: SUBMIT_BUTTON_TEXT_TRANSLATED = SUBMIT_BUTTON_TEXT,
    [CANCEL_BUTTON_TEXT]: CANCEL_BUTTON_TEXT_TRANSLATED = CANCEL_BUTTON_TEXT,
    [TITLE]: TITLE_TRANSLATED = TITLE,
    [TITLE_SELF]: TITLE_SELF_TRANSLATED = TITLE_SELF,
    [TITLE_PET]: TITLE_PET_TRANSLATED = TITLE_PET,
    [LOOKUP_PAGE_TITLE]: LOOKUP_PAGE_TITLE_TRANSLATED = LOOKUP_PAGE_TITLE,
    [ALL_FIELDS_REQUIRED]: ALL_FIELDS_REQUIRED_TRANSLATED = ALL_FIELDS_REQUIRED
  } = translatedItems;

  return {
    DESCRIPTION: DESCRIPTION_TRANSLATED,
    DESCRIPTION_SELF: DESCRIPTION_SELF_TRANSLATED,
    DESCRIPTION_PET: DESCRIPTION_PET_TRANSLATED,
    PAGE_TYPE: PAGE_TYPE,
    SUBMIT_BUTTON_TEXT: SUBMIT_BUTTON_TEXT_TRANSLATED,
    CANCEL_BUTTON_TEXT: CANCEL_BUTTON_TEXT_TRANSLATED,
    TITLE: TITLE_TRANSLATED,
    TITLE_SELF: TITLE_SELF_TRANSLATED,
    TITLE_PET: TITLE_PET_TRANSLATED,
    LOOKUP_PAGE_TITLE: LOOKUP_PAGE_TITLE_TRANSLATED,
    ALL_FIELDS_REQUIRED: ALL_FIELDS_REQUIRED_TRANSLATED,
    // Keep safeJsonParse for complex objects that need JSON parsing
    PERSONAL_INFO_ERROR_TITLES: safeJsonParse(
      translatedItems[JSON.stringify(PERSONAL_INFO_ERROR_TITLES)],
      PERSONAL_INFO_ERROR_TITLES
    ),
    PERSONAL_INFO_FORM_HELP_TEXT: safeJsonParse(
      translatedItems[JSON.stringify(PERSONAL_INFO_FORM_HELP_TEXT)],
      PERSONAL_INFO_FORM_HELP_TEXT
    ),
    PERSONAL_INFO_FORM_LABELS: safeJsonParse(
      translatedItems[JSON.stringify(PERSONAL_INFO_FORM_LABELS)],
      PERSONAL_INFO_FORM_LABELS
    ),
    PERSONAL_INFO_VALIDATION: safeJsonParse(
      translatedItems[JSON.stringify(PERSONAL_INFO_VALIDATION)],
      PERSONAL_INFO_VALIDATION
    ),
    DOB_ERROR_MSG: safeJsonParse(translatedItems[JSON.stringify(DOB_ERROR_MSG)], DOB_ERROR_MSG),
    DOB_LABELS: safeJsonParse(translatedItems[JSON.stringify(DOB_LABELS)], DOB_LABELS)
  };
};
